/**
 * Clone-intent storage + post-auth auto-retry (U7).
 *
 * Storage decision (plan DQ-1 deviation — documented): the plan asked for a
 * "short-lived server-side session entry". This stack has NO server-side
 * ephemeral/session store and NO job-runner (consistent with the plan's
 * no-scheduler constraint), so there is nothing to attach a server-side TTL
 * entry to. We use the browser `sessionStorage` instead, guarded by a 15-minute
 * TTL timestamp. `sessionStorage` is per-tab and is NOT restored on a fresh
 * tab, which satisfies DQ-1's "avoid replay on tab restore" requirement
 * strictly better than `localStorage`/cookie (those persist across tab
 * restore and would replay the clone). The TTL is a belt-and-suspenders guard
 * for the same-tab "signed in much later" case.
 *
 * Flow:
 *  1. Anonymous user clicks "Sign in to save" → `persistCloneIntent(slug)`
 *     writes `{ sourceSlug, ts }` to sessionStorage, then the caller routes to
 *     `/login`.
 *  2. After auth the user returns to the source itinerary view. The mount
 *     effect in `useCloneIntentAutoRetry` sees a fresh (<15min) intent + an
 *     authenticated user, fires `itinerary.clone` exactly once WITHOUT the
 *     user re-clicking, clears the intent, and navigates to the new clone's
 *     `/itineraries/<newSlug>/edit`.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';

export const CLONE_INTENT_KEY = 'itineraryCloneIntent';
export const CLONE_INTENT_TTL_MS = 15 * 60 * 1000;

interface CloneIntent {
  sourceSlug: string;
  ts: number;
}

/** Persist a clone intent (called at the "Sign in to save" click). */
export const persistCloneIntent = (sourceSlug: string): void => {
  try {
    const payload: CloneIntent = { sourceSlug, ts: Date.now() };
    sessionStorage.setItem(CLONE_INTENT_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage unavailable (private mode quota / disabled) — the clone
    // simply will not auto-retry; the user can click again post-auth.
  }
};

/** Remove any stored clone intent. */
export const clearCloneIntent = (): void => {
  try {
    sessionStorage.removeItem(CLONE_INTENT_KEY);
  } catch {
    // no-op
  }
};

/**
 * Read a *fresh* clone intent. A stale intent (older than the TTL, or
 * unparseable) is cleared and treated as absent — this is the tab-restore /
 * "signed in much later" replay guard.
 */
export const readFreshCloneIntent = (): CloneIntent | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(CLONE_INTENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CloneIntent>;
    if (
      typeof parsed.sourceSlug !== 'string' ||
      typeof parsed.ts !== 'number' ||
      Date.now() - parsed.ts > CLONE_INTENT_TTL_MS
    ) {
      clearCloneIntent();
      return null;
    }
    return { sourceSlug: parsed.sourceSlug, ts: parsed.ts };
  } catch {
    clearCloneIntent();
    return null;
  }
};

/**
 * Mount-time post-auth auto-retry. Mount this on the itinerary view page: when
 * an authenticated user lands with a fresh clone intent, it fires the clone
 * exactly once and routes to the new clone's `/edit`. The `firedRef` guard
 * makes this idempotent across re-renders / StrictMode double-invoke.
 */
export const useCloneIntentAutoRetry = (): void => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const cloneMutation = trpc.itinerary.clone.useMutation();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !isAuthenticated) return;
    const intent = readFreshCloneIntent();
    if (!intent) return;

    firedRef.current = true;
    // Clear BEFORE the network call so a failure cannot leave a replayable
    // intent that double-clones on the next mount.
    clearCloneIntent();
    cloneMutation.mutate(
      { sourceSlug: intent.sourceSlug },
      {
        onSuccess: (clone) => {
          navigate(`/itineraries/${clone.publicSlug}/edit`);
        },
        onError: () => {
          // Auto-retry failed (e.g. source no longer viewable). Stay on the
          // page; the user can use the affordance again manually.
          firedRef.current = false;
        },
      }
    );
  }, [isAuthenticated, cloneMutation, navigate]);
};
